import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Card, CardContent, Chip, Collapse, IconButton, Typography } from '@mui/material';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Protocol } from '../../../types';

interface ProtocolCardProps {
  protocol: Protocol;
}

const ProtocolCard = ({ protocol }: ProtocolCardProps) => {
  const [expanded, setExpanded] = useState(false);

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      medical: 'Médico',
      surgical: 'Quirúrgico',
      cardiac: 'Cardíaco',
      emergency: 'Emergencia',
      pediatric: 'Pediatría',
      geriatric: 'Geriatría',
    };
    return labels[category] || category;
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'default' | 'error' => {
    switch (status) {
      case 'active':
        return 'success';
      case 'draft':
        return 'warning';
      case 'archived':
        return 'default';
      case 'deprecated':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      active: 'Activo',
      draft: 'Borrador',
      archived: 'Archivado',
      deprecated: 'Deprecado',
    };
    return labels[status] || status;
  };

  // Ensure we have a valid ID before creating the link
  if (!protocol._id) {
    return null;
  }

  return (
    <Card
      component={Link}
      to={`/protocols/${protocol._id}`}
      sx={{
        mb: 2,
        borderLeft: '5px solid',
        borderColor: 'primary.main',
        boxShadow: 3,
        cursor: 'pointer',
        textDecoration: 'none',
        '&:hover': {
          boxShadow: 6,
          textDecoration: 'none',
        },
      }}
    >
      <CardContent>
        <Box display='flex' justifyContent='space-between' alignItems='flex-start' mb={1}>
          <Box flex={1}>
            <Typography variant='body2' color='text.secondary' gutterBottom>
              Código: {protocol.protocolId}
            </Typography>
            <Typography variant='h6' fontWeight='bold' color='primary' gutterBottom>
              {protocol.title}
            </Typography>
          </Box>
          <IconButton
            size='small'
            onClick={e => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Box display='flex' gap={1} flexWrap='wrap' mb={2}>
          <Chip
            label={getCategoryLabel(protocol.category)}
            size='small'
            color='primary'
            variant='outlined'
          />
          <Chip
            label={getStatusLabel(protocol.status)}
            size='small'
            color={getStatusColor(protocol.status)}
          />
          <Chip label={`v${protocol.version}`} size='small' variant='outlined' />
        </Box>

        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant='body2' color='text.secondary'>
            {new Date(protocol.updatedAt).toLocaleDateString('es-ES')}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {protocol.viewCount || 0} vistas
          </Typography>
        </Box>

        <Collapse in={expanded} timeout='auto' unmountOnExit>
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant='body2' color='text.secondary' paragraph>
              {protocol.description}
            </Typography>
            {protocol.tags && protocol.tags.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {protocol.tags.map(tag => (
                  <Chip key={tag} label={tag} size='small' variant='outlined' />
                ))}
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default ProtocolCard;
